import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { CategoryType } from "@/types";
import { categories, casts, episodes, getEpisodesByCast } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EpisodeCard from "@/components/feature/EpisodeCard";
import CastCard from "./components/CastCard";
import CategoryFilterBar from "./components/CategoryFilterBar";

const PAGE_SIZE = 4;
const VALID_TYPES: CategoryType[] = ["synopsis", "cast", "updates"];

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const activeType = VALID_TYPES.includes(type as CategoryType) ? (type as CategoryType) : null;
  const meta = activeType ? categories[activeType] : null;

  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState<string>(activeType === "updates" ? "desc" : "asc");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useSeo(
    meta ? `${meta.title} - 出轨同学会` : "分类页 - 出轨同学会",
    meta ? meta.description : "出轨同学会影视内容站分类页。",
    meta ? meta.keywords : undefined,
  );

  useEffect(() => {
    setSort(activeType === "updates" ? "desc" : "asc");
    setKeyword("");
    setVisible(PAGE_SIZE);
  }, [activeType]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [keyword, sort]);

  const filteredEpisodes = useMemo(() => {
    let list = [...episodes];
    const query = keyword.trim().toLowerCase();
    if (query) {
      list = list.filter((episode) =>
        [episode.title, episode.summary, ...episode.tags].join(" ").toLowerCase().includes(query),
      );
    }
    list.sort((a, b) => (sort === "asc" ? a.number - b.number : b.number - a.number));
    return list;
  }, [keyword, sort]);

  const filteredCast = useMemo(() => {
    const query = keyword.trim().toLowerCase();
    if (!query) return casts;
    return casts.filter((member) =>
      [member.name, member.role, member.bio, ...member.tags].join(" ").toLowerCase().includes(query),
    );
  }, [keyword]);

  if (!activeType || !meta) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-20 md:px-8 lg:px-12">
        <div className="flex flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-2xl leading-none"></i>
          </span>
          <h1 className="font-heading text-xl font-bold text-foreground-900">分类不存在</h1>
          <p className="font-label text-sm text-foreground-500">
            你访问的分类可能已被移除，请返回首页重新选择。
          </p>
          <Link
            to="/"
            className="rounded-md bg-primary-700 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-800"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const isCast = activeType === "cast";
  const total = isCast ? filteredCast.length : filteredEpisodes.length;
  const visibleCast = filteredCast.slice(0, visible);
  const visibleEpisodes = filteredEpisodes.slice(0, visible);
  const hasMore = total > visible;

  const handleReset = () => {
    setKeyword("");
    setSort(activeType === "updates" ? "desc" : "asc");
    setVisible(PAGE_SIZE);
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8 md:py-8 lg:px-12">
      <Breadcrumb items={[{ label: meta.title }]} />

      <header className="mt-5 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i
              className={
                activeType === "cast"
                  ? "ri-user-star-line text-xl leading-none"
                  : activeType === "updates"
                    ? "ri-calendar-check-line text-xl leading-none"
                    : "ri-book-open-line text-xl leading-none"
              }
            ></i>
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-900">{meta.title}</h1>
        </div>
        <p className="mt-3 max-w-3xl font-label text-sm leading-relaxed text-foreground-600">
          {meta.description}
        </p>
        <p className="mt-2 font-label text-xs text-foreground-400">{meta.subtitle}</p>
      </header>

      <div className="mt-6">
        <CategoryFilterBar
          type={activeType}
          keyword={keyword}
          sort={sort}
          total={total}
          keywordPlaceholder={isCast ? "输入演员姓名或角色名" : "输入关键词，如：同学会、天台"}
          onKeywordChange={setKeyword}
          onSortChange={setSort}
          onReset={handleReset}
        />
      </div>

      <div className="mt-6">
        {total === 0 ? (
          <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-2xl leading-none"></i>
            </span>
            <h2 className="font-heading text-lg font-semibold text-foreground-900">暂无相关内容</h2>
            <p className="max-w-sm font-label text-sm text-foreground-500">
              没有找到符合当前筛选条件的{meta.title}，试试更换关键词或重置筛选条件。
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              重置筛选条件
            </button>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {isCast
                ? visibleCast.map((member) => {
                    const related = getEpisodesByCast(member.id);
                    return (
                      <CastCard
                        key={member.id}
                        member={member}
                        episodeId={related[0]?.id}
                      />
                    );
                  })
                : visibleEpisodes.map((episode) => (
                    <EpisodeCard key={episode.id} episode={episode} rank={episode.number} />
                  ))}
            </div>

            <div className="mt-8 flex flex-col items-center gap-3">
              {hasMore ? (
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-6 py-2.5 font-label text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-down-circle-line text-base leading-none"></i>
                  </span>
                  加载更多（已显示 {visible} / {total}）
                </button>
              ) : (
                <p className="font-label text-xs text-foreground-400">已全部加载完毕</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}